import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  Globe, 
  Users,
  Award,
  Building2,
  Handshake,
  TreePine,
  Rocket,
  Leaf,
  Crown,
  Heart,
  Lightbulb,
  Target,
  Briefcase,
  Sparkles,
  ArrowRight,
  Calendar
} from "lucide-react";
import AboutHero from "../assets/images/HS_UN.jpeg";
import CowganicImage from "../assets/images/cowganic_logo.png";
import ShantiLogo from "../assets/images/Shanti_logo.jpg";
import Rayspeech from "../assets/images/Ray_speech.jpg";
import ICACeleb from "../assets/images/ICA_Celeb.jpg";
import Kaizenith_Logo from "../assets/images/Kai_Logo_Raw.jpg";
import YMA_Speech from "../assets/images/Vidhansabha_sitting.jpg";
import YouthSummit from "../assets/images/journey/YouthSummit_Y20.jpg";
import ClimateAction from "../assets/images/journey/674d937df14211449369699b.jpg";
import CooperativeWork from "../assets/images/journey/674d99c9318ae90917879819.jpg";
import GlobalLeadership from "../assets/images/journey/515442298_18105262984528706_8966027717628310076_n.jpg";
import BusinessInnovation from "../assets/images/Ray_speech.jpg";

// Counter Animation Hook
const useCountAnimation = (end, duration = 2, startWhen = true) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startWhen) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, startWhen]);

  return count;
};

const AnimatedCounter = ({ end, suffix = "" }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const count = useCountAnimation(end, 2, inView);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Hero Section
const EditorialHero = ({ isDark }) => {
  return (
    <section className="min-h-screen overflow-hidden bg-white">
      {/* Big Container */}
      <div className="flex flex-col lg:flex-row min-h-screen max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 gap-8 sm:gap-12 lg:gap-16 items-center">
        {/* Left Container - Text */}
        <div className="w-full lg:w-1/2 flex items-center order-2 lg:order-1">
          <div className="w-full">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-full"
              >
                <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 mr-1.5 sm:mr-2" />
                <span className="text-orange-700 text-xs sm:text-sm font-semibold">First Indian ICA YC President in 130 Years</span>
              </motion.div>

              {/* Main Title */}
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight"
                >
                  <span className="text-gray-900">Harsh</span>
                  <br />
                  <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">Sanghani</span>
                </motion.h1>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed max-w-lg"
              >
                Transforming communities across 110+ countries through sustainable innovation, youth empowerment, and international cooperation.
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8 pt-4"
              >
                <div>
                  <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                    Global
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Reach</div>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                    Historic
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Leadership</div>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                    Multi
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Sector</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Right Container - Rounded Image */}
        <div className="w-full lg:w-1/2 flex items-center justify-center order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative w-full"
          >
            <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl sm:rounded-3xl opacity-20 blur-2xl"></div>
            <div className="relative w-full h-[350px] sm:h-[450px] md:h-[500px] lg:h-[600px] rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border-2 sm:border-4 border-white">
              <img
                src={AboutHero}
                alt="Harsh Sanghani - Global Youth Leader"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Story Section - Magazine Layout
const StorySection = ({ isDark }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="mb-12 sm:mb-16 lg:mb-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
            <div className="col-span-1 lg:col-span-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 leading-tight">
                The
                <br />
                <span className="text-red-600">Journey</span>
              </h2>
            </div>
            <div className="col-span-1 lg:col-span-8 flex items-end">
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                From a 14-year-old with a vision to change agriculture to the youngest global leader 
                representing 110+ countries. Every chapter has been about turning impossible into inevitable.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Timeline Stories */}
        <div className="space-y-12 sm:space-y-16 lg:space-y-24">
          {/* 2019 - Cowganic */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center"
          >
            <div className="col-span-1 lg:col-span-6">
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-red-600 rounded-full">
                    <TreePine className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">2019</div>
                    <div className="text-xs sm:text-sm text-gray-600">Age 14</div>
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Cowganic: The Beginning</h3>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                  While peers were focused on exams, I was obsessed with a question: 
                  How can farming feed the world without destroying it? Cowganic was born 
                  from this obsession—proving that sustainable agriculture isn't just possible, it's profitable.
                </p>
              </div>
            </div>
            <div className="col-span-1 lg:col-span-6">
              <img
                src={CowganicImage}
                alt="Sustainable Agriculture"
                className="w-full h-48 sm:h-64 md:h-72 lg:h-80 object-cover rounded-xl sm:rounded-2xl shadow-lg"
              />
            </div>
          </motion.div>

          {/* 2022 - Shanti Herbals */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center"
          >
            <div className="col-span-1 lg:col-span-6 lg:order-2">
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-red-600 rounded-full">
                    <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">2022</div>
                    <div className="text-xs sm:text-sm text-gray-600">Age 17</div>
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Shanti Herbals: Scaling Impact</h3>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                  Success in agriculture taught me that real change happens when you connect dots others don't see. 
                  Shanti Herbals merged ancient Ayurvedic wisdom with modern wellness needs, 
                  taking Indian heritage to global markets.
                </p>
              </div>
            </div>
            <div className="col-span-1 lg:col-span-6 lg:order-1">
              <img
                src={ShantiLogo}
                alt="Health Innovation"
                className="w-full h-48 sm:h-64 md:h-72 lg:h-80 object-cover rounded-xl sm:rounded-2xl shadow-lg"
              />
            </div>
          </motion.div>

          {/* 2022 - Yuva Model Assembly */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center"
          >
            <div className="col-span-1 lg:col-span-6">
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-red-600 rounded-full">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">2022</div>
                    <div className="text-xs sm:text-sm text-gray-600">Age 17</div>
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Yuva Model Assembly: Youth Governance</h3>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                  Recognizing that youth voices needed structured platforms for meaningful participation, 
                  I established the Yuva Model Assembly. This initiative created democratic spaces where 
                  young leaders could engage with policy-making and governance at multiple levels.
                </p>
              </div>
            </div>
            <div className="col-span-1 lg:col-span-6">
              <img
                src={YMA_Speech}
                alt="Youth Assembly"
                className="w-full h-48 sm:h-64 md:h-72 lg:h-80 object-cover rounded-xl sm:rounded-2xl shadow-lg"
              />
            </div>
          </motion.div>

          {/* 2024 - Infrastructure */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center"
          >
            <div className="col-span-1 lg:col-span-6 lg:order-2">
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-red-600 rounded-full">
                    <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">2024</div>
                    <div className="text-xs sm:text-sm text-gray-600">Age 19</div>
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Ray Infrastructure: Building Tomorrow</h3>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                  Every successful venture taught me something new about scale and impact. 
                  Ray Infrastructure became the platform where sustainable development met cutting-edge innovation, 
                  proving that environmental responsibility drives profitability.
                </p>
              </div>
            </div>
            <div className="col-span-1 lg:col-span-6 lg:order-1">
              <img
                src={Rayspeech}
                alt="Infrastructure Development"
                className="w-full h-48 sm:h-64 md:h-72 lg:h-80 object-cover rounded-xl sm:rounded-2xl shadow-lg"
              />
            </div>
          </motion.div>

         

          {/* 2025 - ICA YC Presidency */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center"
          >
            <div className="col-span-1 lg:col-span-6">
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-red-600 rounded-full">
                    <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">2025</div>
                    <div className="text-xs sm:text-sm text-gray-600">Age 20</div>
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Making History: ICA YC Presidency</h3>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                  Six years of relentless work led to this moment: becoming the first Indian President 
                  of the ICA Youth Committee in 130 years. Now representing youth voices across 110+ countries, 
                  turning local impact into global transformation.
                </p>
              </div>
            </div>
            <div className="col-span-1 lg:col-span-6">
              <img
                src={ICACeleb}
                alt="Global Leadership"
                className="w-full h-48 sm:h-64 md:h-72 lg:h-80 object-cover rounded-xl sm:rounded-2xl shadow-lg"
              />
            </div>
          </motion.div>

          {/* 2025 - Kaizenith Technologies */}


          {/* 2025 - Kaizenith Technologies */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center"
          >
            <div className="col-span-1 lg:col-span-6 lg:order-2">
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-red-600 rounded-full">
                    <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">2025</div>
                    <div className="text-xs sm:text-sm text-gray-600">Age 20</div>
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Kaizenith Technologies: Innovation Frontier</h3>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                  Technology became the bridge between vision and execution. Kaizenith Technologies 
                  emerged as the platform where cutting-edge innovation meets social impact, 
                  developing solutions that scale positive change across industries and communities.
                </p>
              </div>
            </div>
            <div className="col-span-1 lg:col-span-6 lg:order-1">
              <img
                src={Kaizenith_Logo}
                alt="Technology Innovation"
                className="w-full h-48 sm:h-64 md:h-72 lg:h-80 object-cover rounded-xl sm:rounded-2xl shadow-lg"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Expertise Areas - Card Layout
const ExpertiseSection = ({ isDark }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const areas = [
    {
      title: "Social Development",
      description: "Empowering youth voices through global representation and community-building initiatives.",
      icon: Users,
      color: "bg-red-600",
      image: YouthSummit,
      highlights: ["ICA Youth President", "UN Forum Representation", "Community Development"]
    },
    {
      title: "Business Innovation",
      description: "Building sustainable enterprises that prove profit and purpose create powerful synergies.",
      icon: Rocket,
      color: "bg-red-600",
      image: BusinessInnovation,
      highlights: ["Agro-Herbal Ventures", "Infrastructure Projects", "Technology Innovation"]
    },
    {
      title: "Climate Action",
      description: "Leading environmental initiatives through carbon credits and international climate diplomacy.",
      icon: Leaf,
      color: "bg-red-600",
      image: ClimateAction,
      highlights: ["Carbon Credit Platforms", "Renewable Energy", "Climate Diplomacy"]
    },
    {
      title: "Cooperative Movement",
      description: "Advancing the third way of economy through digital platforms and policy advocacy.",
      icon: Handshake,
      color: "bg-red-600",
      image: CooperativeWork,
      highlights: ["ICA Global Board", "Coopathon Movement", "Policy Development"]
    },
    {
      title: "International Relations",
      description: "Building bridges between nations through trade diplomacy and youth representation.",
      icon: Globe,
      color: "bg-red-600",
      image: GlobalLeadership,
      highlights: ["Trade Diplomacy", "Policy Influence", "Global Partnerships"]
    }
  ];

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16 lg:mb-20"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-3 sm:mb-4 lg:mb-6">
            Areas of <span className="text-red-600">Expertise</span>
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-700 max-w-3xl mx-auto px-4">
            Five interconnected domains where innovation meets impact, 
            creating lasting change across communities and continents.
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {areas.map((area, index) => (
            <motion.div
              key={area.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className={`group ${index === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}
            >
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                {/* Image */}
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img
                    src={area.image}
                    alt={area.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/20" />
                  <div className={`absolute top-3 left-3 sm:top-4 sm:left-4 p-1.5 sm:p-2 ${area.color} rounded-lg`}>
                    <area.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">{area.title}</h3>
                  <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">{area.description}</p>
                  
                  <div className="space-y-1.5 sm:space-y-2">
                    {area.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-600 rounded-full flex-shrink-0" />
                        <span className="text-xs sm:text-sm text-gray-600">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Global Impact Showcase - Redesigned
const ImpactStatement = ({ isDark }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-24 bg-gray-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(239, 68, 68, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(239, 68, 68, 0.3) 0%, transparent 50%)`
        }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center space-y-16"
        >
          {/* Main Title */}
          <div className="space-y-4 sm:space-y-6">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black text-white leading-tight px-4">
              Impact That <span className="text-red-500">Transcends Borders</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed px-4">
              Six years of transforming vision into reality, one community at a time, 
              across continents and cultures.
            </p>
          </div>

          {/* Achievement Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {[
              { 
                number: "110+", 
                label: "Countries", 
                desc: "Youth voices represented through ICA and UN platforms",
                icon: Globe,
                image: "https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?q=80&w=300&h=200&auto=format&fit=crop"
              },
              { 
                number: "130", 
                label: "Years", 
                desc: "Historic first Indian to lead ICA Youth Committee",
                icon: Crown,
                image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=300&h=200&auto=format&fit=crop"
              },
              { 
                number: "5", 
                label: "Sectors", 
                desc: "From agriculture to international diplomacy",
                icon: Target,
                image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=300&h=200&auto=format&fit=crop"
              },
              { 
                number: "2019", 
                label: "Started", 
                desc: "Continuous innovation since age 14",
                icon: Rocket,
                image: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?q=80&w=300&h=200&auto=format&fit=crop"
              }
            ].map((achievement, index) => (
              <motion.div
                key={achievement.label}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.8 }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
                  {/* Background Image */}
                  <div className="absolute inset-0 opacity-20">
                    <img
                      src={achievement.image}
                      alt={achievement.label}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  
                  <div className="relative p-6 sm:p-8 text-center space-y-3 sm:space-y-4">
                    <achievement.icon className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto" />
                    <div className="text-3xl sm:text-4xl font-black text-white">
                      {achievement.number}
                    </div>
                    <div className="text-base sm:text-lg font-bold text-red-400">
                      {achievement.label}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                      {achievement.desc}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Philosophy Statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="p-6 sm:p-8 lg:p-12 bg-white/5 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-white/10">
              <Sparkles className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto mb-6 sm:mb-8" />
              <blockquote className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-4 sm:mb-6 leading-relaxed px-4">
                "Success isn't measured by the heights you reach, but by the lives you lift 
                and the communities you transform along the way."
              </blockquote>
              <cite className="text-red-400 font-semibold text-base sm:text-lg">— Harsh Sanghani</cite>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Vision & Contact - Split Layout
const VisionContact = ({ isDark }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 xl:gap-16">
          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.8 }}
            className="space-y-4 sm:space-y-6 lg:space-y-8"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900">
              Looking <span className="text-red-600">Forward</span>
            </h2>
            
            <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
              The journey so far has been about proving what's possible when youth, 
              innovation, and purpose align. The road ahead is about scaling that impact 
              to transform how the world approaches cooperation, sustainability, and shared prosperity.
            </p>

            <div className="space-y-4 sm:space-y-6">
              {[
                { icon: Target, title: "Expanding Cooperative Economics", desc: "Building the third way beyond capitalism and socialism" },
                { icon: Globe, title: "Global Youth Network", desc: "Connecting changemakers across all continents" },
                { icon: Leaf, title: "Climate Innovation", desc: "Scaling environmental solutions through cooperation" }
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-start space-x-3 sm:space-x-4"
                >
                  <div className="p-1.5 sm:p-2 bg-red-600 rounded-lg flex-shrink-0">
                    <item.icon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-4 sm:space-y-6 lg:space-y-8"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900">
              Let's <span className="text-red-600">Connect</span>
            </h2>
            
            <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
              Whether you're interested in collaboration, partnership, or simply want to 
              discuss ideas that can change the world, I'm always open to meaningful conversations.
            </p>

            {/* Contact Info */}
            <div className="space-y-4 sm:space-y-6">
              <div className="p-4 sm:p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Get in Touch</h3>
                <div className="space-y-2 sm:space-y-3">
                  <div className="text-sm sm:text-base text-gray-700 break-all">harshsanghani2004@gmail.com</div>
                  <div className="text-gray-600 text-xs sm:text-sm">Available globally • Based in India</div>
                </div>
              </div>

              <div className="p-4 sm:p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Areas of Interest</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-600 rounded-full flex-shrink-0" />
                    <span className="text-gray-600">Global Partnerships</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-600 rounded-full flex-shrink-0" />
                    <span className="text-gray-600">Youth Initiatives</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-600 rounded-full flex-shrink-0" />
                    <span className="text-gray-600">Sustainable Business</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-600 rounded-full flex-shrink-0" />
                    <span className="text-gray-600">Climate Solutions</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-600 rounded-full flex-shrink-0" />
                    <span className="text-gray-600">Cooperative Movement</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-600 rounded-full flex-shrink-0" />
                    <span className="text-gray-600">Policy Development</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Main Component
const AboutPage = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <main>
        <EditorialHero isDark={isDark} />
        <StorySection isDark={isDark} />
        <ExpertiseSection isDark={isDark} />
        <ImpactStatement isDark={isDark} />
        <VisionContact isDark={isDark} />
      </main>
    </div>
  );
};

export default AboutPage;